/* eslint-disable i18next/no-literal-string */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Fab, Paper, Typography, useTheme } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { faGear } from "@fortawesome/pro-light-svg-icons";
import { faArrowRight, faCog } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";

import { UtfDateRangePicker } from "@components";

import ControlPanelItem from "../IndoorClimate/ControlPanelItem";
import QuickOptionButtonGroup from "./QuickOptionButtonGroup";
import type { DateRange, GraphSettings, Resolution } from "./types";

export type { Resolution };

type ResolutionOption = {
  label: string;
  value: Resolution;
  enabled: boolean;
  units: [string, number[]][];
};

export const RESOLUTION_OPTIONS: Record<Resolution, ResolutionOption> = {
  as_measured: { label: "text_as_measured", value: "as_measured", enabled: false, units: null },
  hourly: {
    label: "text_hourly",
    value: "hourly",
    enabled: true,
    units: [["hour", [1, 3, 6, 12]]],
  },
  daily: { label: "text_daily", value: "daily", enabled: true, units: [["day", [1]]] },
};

type ControlPanelProps = {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  onDateRangeChange?: (startDate: DateTime | null, endDate: DateTime | null) => void;
  onResolutionChange?: (resolution: Resolution) => void;
  onGraphSettingsChange?: (settings: GraphSettings) => void;
  initialDateRange?: DateRange;
  hiddenResolutions?: Resolution[];
  children?: React.ReactNode;
  shouldShowDefaultPanel?: boolean;
  title?: string;
};

export const QUICK_DATE_OPTIONS = [
  { label: "text_last_day", value: "lastDay" },
  { label: "text_last_week", value: "lastWeek" },
  { label: "text_last_month", value: "lastMonth" },
];

const ControlPanel: React.FC<ControlPanelProps> = ({
  isOpen = true,
  onToggle,
  onDateRangeChange,
  onResolutionChange,
  initialDateRange,
  hiddenResolutions = [],
  children,
  shouldShowDefaultPanel = true,
  title = "Settings",
}) => {
  const theme = useTheme();
  const { t } = useTranslation("_common");

  // Filter out hidden resolutions
  const availableResolutions = Object.values(RESOLUTION_OPTIONS).filter(
    (option) => !hiddenResolutions.includes(option.value)
  );

  // Get initial resolution
  const getInitialResolution = (): Resolution => {
    const defaultResolution: Resolution = "hourly";
    if (!hiddenResolutions.includes(defaultResolution)) {
      return defaultResolution;
    }
    return availableResolutions.length > 0 ? availableResolutions[0].value : "hourly";
  };

  // State variables
  const [isPanelOpen, setIsPanelOpen] = useState(isOpen);
  const [timeRange, setTimeRange] = useState("lastWeek");
  const [resolution, setResolution] = useState<Resolution>(getInitialResolution());
  const [startDate, setStartDate] = useState<DateTime | null>(
    initialDateRange?.startDate || DateTime.now().minus({ days: 7 })
  );
  const [endDate, setEndDate] = useState<DateTime | null>(
    initialDateRange?.endDate || DateTime.now()
  );

  // Sync with external open state
  useEffect(() => {
    setIsPanelOpen(isOpen);
  }, [isOpen]);

  const togglePanel = useCallback(() => {
    const newState = !isPanelOpen;
    setIsPanelOpen(newState);
    onToggle?.(newState);
  }, [isPanelOpen, onToggle]);

  const handleTimeRangeChange = useCallback(
    (newTimeRange: string) => {
      setTimeRange(newTimeRange);

      // Calculate date range based on selected option
      const now = DateTime.now();
      let start = null;
      let end = null;

      switch (newTimeRange) {
        case "lastDay":
          start = now.minus({ days: 1 });
          end = now;
          break;
        case "lastWeek":
          start = now.minus({ weeks: 1 });
          end = now;
          break;
        case "lastMonth":
          start = now.minus({ months: 1 });
          end = now;
          break;
      }

      setStartDate(start);
      setEndDate(end);

      if (onDateRangeChange) {
        onDateRangeChange(start, end);
      }
    },
    [onDateRangeChange]
  );

  const handleResolutionChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value as string;
      setResolution(value as Resolution);
      onResolutionChange?.(value as Resolution);
    },
    [onResolutionChange]
  );

  // Collapsed
  if (!isPanelOpen) {
    return (
      <Fab
        aria-label="Open settings panel"
        onClick={togglePanel}
        size="medium"
        sx={{
          position: "absolute",
          top: theme.spacing(1),
          right: theme.spacing(2),
          borderRadius: theme.spacing(1),
        }}
        color="secondary"
      >
        <FontAwesomeIcon icon={faGear} size="lg" color={theme.palette.background.default} />
      </Fab>
    );
  }

  // Expanded
  return (
    <Paper
      data-testid="control-panel"
      elevation={0}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        width: "330px",
        height: "100%",
        flexShrink: 0,
      }}
    >
      {/* Header with title and close button to the right */}
      <Box
        data-testid="control-panel-header"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          minHeight: "48px",
          top: 0,
          position: "sticky",
        }}
        component={Paper}
      >
        <Box sx={{ display: "flex", position: "sticky", alignItems: "center", p: 1.5 }}>
          <FontAwesomeIcon icon={faCog} style={{ marginRight: "8px" }} />
          <Typography variant="titleSmall" data-testid="control-panel-title">
            {title}
          </Typography>
        </Box>
        <Box
          data-testid="control-panel-close"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "secondary.main",
            width: "48px",
            height: "100%",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "secondary.main",
            },
          }}
          onClick={togglePanel}
        >
          <FontAwesomeIcon icon={faArrowRight} size="lg" color={theme.palette.background.default} />
        </Box>
      </Box>

      {/* Content Area */}
      <Box sx={{ height: "100%", overflowY: "auto" }} py={2}>
        {children}
        {/* Data Settings Section */}
        {shouldShowDefaultPanel && (
          <ControlPanelItem title="Data settings">
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="labelMedium" sx={{ display: "block", mb: 1 }}>
                Quick options
              </Typography>
              <Box sx={{ mb: 3 }}>
                <QuickOptionButtonGroup
                  ariaLabel="Time range quick options"
                  options={QUICK_DATE_OPTIONS}
                  activeValue={timeRange}
                  onChange={(val) => handleTimeRangeChange(val.value)}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="labelMedium" sx={{ display: "block", mb: 1 }}>
                  Quick options
                </Typography>
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                  data-testid="control-panel-date-selection"
                >
                  <UtfDateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={DateTime.now()}
                    onChange={(start, end) => {
                      setStartDate(start);
                      setEndDate(end);
                      setTimeRange("custom");
                      if (onDateRangeChange) {
                        onDateRangeChange(start, end);
                      }
                    }}
                    size="small"
                    sx={{
                      minWidth: 120,
                      "& .MuiSelect-select": {
                        py: 1,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="labelMedium" sx={{ display: "block", mb: 1 }}>
                  Resolution
                </Typography>
                <Select
                  data-testid="control-panel-resolution-select"
                  value={resolution}
                  onChange={handleResolutionChange}
                  fullWidth
                  size="small"
                  sx={{
                    minWidth: 120,
                    "& .MuiSelect-select": {
                      py: 1,
                    },
                  }}
                >
                  {availableResolutions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </ControlPanelItem>
        )}
      </Box>
    </Paper>
  );
};

export default ControlPanel;
